import { useState, type ReactNode } from "react";

import { makePostApiCall, makePutApiCall } from "../../../shared/makeApiCall";
import { useToast } from "../../../contexts/ToastContext";
import UserDetailsForm from "../../../components/forms/user/UserDetailsForm";
import type { ApiMessage } from "../../../types/api";
import type {
  CreateUserPayload,
  ResetUserPasswordPayload,
  UserEntity,
  UserListApiPage,
  UserMutationPayload,
  UserPage,
  UserRecord,
} from "../../../types/users";
import { normalizeUserRecord } from "./normalizeUserRecord";

interface SlideOverState { childComponent: ReactNode; open: boolean }

export function UsersViewModel(logout: () => void, canManageMapping = false) {
  const { showSuccess } = useToast();
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<UserPage>({
    data: [], current_page: 1, last_page: 1, per_page: 20, total: 0,
  });

  const [slideOver, setSlideOver] = useState<SlideOverState>({
    childComponent: null,
    open: false,
  });

  const getUsers = async function (search: string | null, page: number) {
    setLoading(true);
    const payload = {
      page: page,
      search: search,
    };
    const callResult = await makePostApiCall<UserListApiPage, typeof payload>(`users`, payload);
    setLoading(false);
    if (callResult.success) {
      const mappedUsers = callResult.data.data.map(normalizeUserRecord);
      setUsers({ ...callResult.data, data: mappedUsers });
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const createUser = async function (values: CreateUserPayload) {
    const callResult = await makePostApiCall<UserEntity, CreateUserPayload>("createUser", values);

    if (callResult.success) {
      showSuccess("User created");
      setSlideOver({
        childComponent: null,
        open: false,
      });
      setTimeout(function () {
        getUsers("", 1);
      }, 1000);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const updateUser = async function (user: UserRecord, values: UserMutationPayload) {
    const callResult = await makePutApiCall<UserEntity, UserMutationPayload>(`users/${user.id}`, values);

    if (callResult.success) {
      showSuccess("User updated");
      setSlideOver({
        childComponent: null,
        open: false,
      });
      setTimeout(function () {
        getUsers("", 1);
      }, 1000);
    } else {
      if (callResult.errorStatus === 401) {
        logout();
      }
    }
  };

  const resetUserPassword = async function (user: UserRecord, values: ResetUserPasswordPayload) {
    const callResult = await makePostApiCall<ApiMessage, ResetUserPasswordPayload>(`/users/reset-password/${user.id}`, values);
    if (callResult.success) {
      showSuccess(callResult.data.message || "Password updated");
    } else if (callResult.errorStatus === 401) {
      logout();
    }
  };

  const openAddUserSlideOver = () => {
    setSlideOver({
      childComponent: (
        <UserDetailsForm
          canManageMapping={canManageMapping}
          isEdit={false}
          panelMode={true}
          onCancel={() => setSlideOver({ childComponent: null, open: false })}
          actionCall={createUser}
        />
      ),
      open: true,
    });
  };

  const viewUserDetails = function (user: UserRecord, index: number) {
    setSlideOver({
      childComponent: (
        <UserDetailsForm
          canManageMapping={canManageMapping}
          user={user}
          index={index}
          isEdit={true}
          panelMode={true}
          onCancel={() => setSlideOver({ childComponent: null, open: false })}
          actionCall={(values: UserMutationPayload) => updateUser(user, values)}
          onResetPassword={(values: ResetUserPasswordPayload) => resetUserPassword(user, values)}
        />
      ),
      open: true,
    });
  };

  return {
    openAddUserSlideOver,
    getUsers,
    loading,
    users,
    setSlideOver,
    slideOver,
    viewUserDetails,
  };
}
